import React, { useState } from 'react';
import { 
  Home, Search, BookOpen, Trophy, Calendar, User, Settings,
  ChevronDown, ChevronRight, BarChart3, Plus, FileText, ArrowLeft,
  X, GraduationCap
} from 'lucide-react';

export const CourseSidebar = ({ 
  isOpen, 
  onToggle, 
  user, 
  userProfile, 
  isCreator, 
  isExpert,
  bookData,
  customStyling,
  activeView,
  onNavigate
}) => {
  const [expandedSections, setExpandedSections] = useState(new Set(['learning']));

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const isActive = (view) => {
    return activeView === view;
  };

  const handleNavClick = (view) => {
    onNavigate(view);
    // Close mobile sidebar on navigation
    if (window.innerWidth < 1024) {
      onToggle();
    }
  };

  const learningNavItems = [
    { icon: Home, label: 'Dashboard', view: 'dashboard' },
    { icon: Search, label: 'Discover', view: 'discover', description: 'Browse courses' },
    { icon: BookOpen, label: 'My Learning', view: 'my-learning' },
    { 
      icon: Calendar, 
      label: 'My Sessions', 
      view: 'sessions',
      description: 'Virtual sessions',
      badge: userProfile?.upcomingSessions || null
    },
    { icon: Trophy, label: 'XP & Achievements', view: 'xp' }
  ];

  const creatorNavItems = [
    { 
      icon: BarChart3, 
      label: 'Creator Dashboard', 
      view: 'creator',
      description: 'Manage your courses'
    },
    { 
      icon: Plus, 
      label: 'Create Course', 
      view: 'creator-new',
      description: 'Build new course'
    },
    { 
      icon: FileText, 
      label: 'My Courses', 
      view: 'creator-courses',
      description: 'Course management'
    },
    { 
      icon: BarChart3, 
      label: 'Analytics', 
      view: 'creator-analytics',
      description: 'Performance insights'
    }
  ];

  const Header = ({ isOwner, bookData, customStyling, navigate }) => {
      const [isSquare, setIsSquare] = useState(true);
  
      const handleImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        setIsSquare(naturalWidth === naturalHeight);
      };
  
      // Get logo height from storeSettings or use default
      const logoHeight = bookData?.logoHeight || 80;
  
      return (
        <div className="flex items-center gap-2">
          {/* Return Arrow for Album/Book Owner */}
          {isOwner && (
            <button
              onClick={() => navigate("/dashboard")}
              className="p-0 hover:bg-gray-100 rounded-lg transition-colors"
              title="Return to Dashboard"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
          )}
  
          {/* Logo Display with Dynamic Height */}
          {bookData?.logoUrl && (
            <div className="flex-shrink-0">
              <img
                src={bookData.logoUrl}
                alt={`${bookData.title || bookData.botName} logo`}
                style={{ height: `${logoHeight}px` }}
                className="w-auto object-contain rounded-lg"
                onLoad={handleImageLoad}
              />
            </div>
          )}
  
          {/* Title Section (only show if logo is square OR no logo at all) */}
          {(isSquare || !bookData?.logoUrl) && (
            <div className="min-w-0">
              <h1
                className="text-2xl font-bold text-gray-900 truncate"
                style={{ color: customStyling.primaryColor }}
              >
                {bookData.title || bookData.botName}
              </h1>
              {bookData.description && (
                <p className="text-gray-600 mt-1 text-sm line-clamp-2">
                  {bookData.description}
                </p>
              )}
            </div>
          )}
        </div>
      );
    };

  if (!isOpen) {
    return (
      <div className="fixed left-0 top-0 bottom-0 hidden lg:flex w-20 bg-white border-r border-gray-200 flex-col items-center py-4 z-30">
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-gray-100 mb-8"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
        
        {learningNavItems.slice(0, 5).map((item) => (
          <button
            key={item.view}
            onClick={() => handleNavClick(item.view)}
            className={`p-3 rounded-lg mb-2 relative ${
              isActive(item.view)
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            title={item.label}
          >
            <item.icon className="w-5 h-5" />
            {item.badge && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 bottom-0 left-0 z-50 lg:z-30
        w-80 bg-white border-r border-gray-200 shadow-xl  
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => handleNavClick('dashboard')}
              className="flex items-center gap-2"
            >
              <Header 
                isOwner={isCreator} 
                bookData={bookData} 
                customStyling={customStyling}
                navigate={onNavigate}
              />

            </button>
            
            <button
              onClick={onToggle}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Learning Section */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('learning')}
              className="flex items-center justify-between w-full mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              <span>Learning</span>
              {expandedSections.has('learning') ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
            
            {expandedSections.has('learning') && (
              <div className="space-y-1">
                {learningNavItems.map((item) => (
                  <button
                    key={item.view}
                    onClick={() => handleNavClick(item.view)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive(item.view)
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{item.label}</span>
                        {item.badge && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <span className="text-xs text-gray-500 truncate block">
                          {item.description}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Creator Section */}
          {isCreator && (
            <div className="mb-6">
              <button
                onClick={() => toggleSection('creator')}
                className="flex items-center justify-between w-full mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider"
              >
                <span>Creator</span>
                {expandedSections.has('creator') ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {expandedSections.has('creator') && (
                <div className="space-y-1">
                  {creatorNavItems.map((item) => (
                    <button
                      key={item.view}
                      onClick={() => handleNavClick(item.view)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        isActive(item.view)
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-sm font-medium truncate">{item.label}</div>
                        {item.description && (
                          <span className="text-xs text-gray-500 truncate block">
                            {item.description}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Profile - Fixed */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4">
          <button
            onClick={() => handleNavClick('settings')}
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-gray-600" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm font-medium text-gray-900 truncate">
                {userProfile?.name || user?.email || 'User'}
              </div>
              <div className="text-xs text-gray-500">
                {userProfile?.xp || 0} XP
              </div>
            </div>
            <Settings className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>
    </>
  );
};